import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { api, ApiError } from '../lib/api';
import { MaterialSymbol } from './icons';

/**
 * WhatsApp integration card for the Settings → General → Integrations section.
 *
 * Merchant flow: enter number → "Send code" → enter the 6-digit code
 * delivered over WhatsApp → connected. Polls status every 4 s while PENDING
 * so delivery errors (e.g. "not on WhatsApp") surface without a reload.
 *
 * All four states are self-contained; the card matches the Email card's
 * visual language (cg-integration / cg-email-config classes).
 */

type Step = 'loading' | 'idle' | 'code' | 'connected';

function toast(message: string, isError = false): void {
  window.shopify?.toast.show(message, isError ? { isError: true } : undefined);
}

export function WhatsAppCard(): ReactNode {
  const [step, setStep] = useState<Step>('loading');
  const [configured, setConfigured] = useState(false);
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState('');
  const [connectedPhone, setConnectedPhone] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const refresh = async (): Promise<void> => {
    try {
      const res = await api.whatsappStatus();
      setConfigured(res.configured);
      if (res.connection?.status === 'VERIFIED') {
        setConnectedPhone(res.connection.phone);
        setStep('connected');
      } else if (res.connection?.status === 'PENDING') {
        setConnectedPhone(res.connection.phone);
        if (res.connection.lastError) setError(res.connection.lastError);
        setStep('code');
      } else {
        setStep('idle');
      }
    } catch {
      setStep('idle');
    }
  };

  useEffect(() => {
    void refresh();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // While PENDING, poll so the delivery-status webhook error surfaces
  // (e.g. "This phone number isn't on WhatsApp") without a manual reload.
  useEffect(() => {
    if (step !== 'code') return;
    const t = setInterval(() => void refresh(), 4000);
    return () => clearInterval(t);
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  const sendCode = async (): Promise<void> => {
    setBusy(true);
    setError('');
    try {
      const res = await api.whatsappConnect(phone.trim());
      setConnectedPhone(res.phone);
      setStep('code');
      toast('Code sent — check your WhatsApp');
    } catch (e) {
      const msg =
        e instanceof ApiError || e instanceof Error
          ? e.message
          : 'Could not send a code — check the number.';
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  const verify = async (): Promise<void> => {
    setBusy(true);
    setError('');
    try {
      await api.whatsappVerify(code);
      setCode('');
      setStep('connected');
      toast('WhatsApp connected');
    } catch (e) {
      const msg =
        e instanceof ApiError || e instanceof Error ? e.message : 'Incorrect code.';
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async (): Promise<void> => {
    setBusy(true);
    try {
      await api.whatsappDisconnect();
      setPhone('');
      setConnectedPhone('');
      setCode('');
      setError('');
      setStep('idle');
      toast('WhatsApp disconnected');
    } catch (e) {
      toast(
        e instanceof ApiError || e instanceof Error ? e.message : 'Could not disconnect.',
        true,
      );
    } finally {
      setBusy(false);
    }
  };

  const isConnected = step === 'connected';
  const isPending = step === 'code';

  return (
    <>
      {/* ── The card row inside .cg-integrations-grid ── */}
      <div className="cg-integration">
        <div className="cg-int-left">
          <div className="cg-int-icon is-whatsapp">
            <MaterialSymbol name="chat_bubble" />
          </div>
          <div>
            <div className="cg-setting-name">
              WhatsApp
              {isConnected ? (
                <span className="cg-int-connected">Connected</span>
              ) : isPending ? (
                <span className="cg-int-connected" style={{ background: 'rgba(100,100,100,0.12)', color: 'var(--cg-on-surface-variant)' }}>
                  Pending
                </span>
              ) : null}
            </div>
            <div className="cg-setting-desc">
              {step === 'loading'
                ? 'Loading…'
                : isConnected
                  ? `Alerts to +${connectedPhone}`
                  : isPending
                    ? `Verify +${connectedPhone}`
                    : 'Direct mobile alerts'}
            </div>
          </div>
        </div>

        {step !== 'loading' && (
          <button
            type="button"
            className="cg-connect-btn"
            onClick={() => setOpen((o) => !o)}
          >
            {open
              ? 'Close'
              : isConnected
                ? 'Manage'
                : isPending
                  ? 'Verify'
                  : 'Connect'}
          </button>
        )}
      </div>

      {/* ── Expanded config panel (mirrors .cg-email-config).
           grid-column: 1/-1 spans both columns of .cg-integrations-grid
           so this panel breaks out of the 2-col layout, same as the email
           config which renders below the grid in SettingsPage. ── */}
      {open && step !== 'loading' && (
        <div className="cg-email-config" style={{ gridColumn: '1 / -1' }}>
          {!configured && (
            <p className="cg-email-config-hint">
              WhatsApp messaging isn't active yet — add the{' '}
              <code style={{ fontSize: 'inherit' }}>WHATSAPP_*</code> keys to the
              server and restart to enable this integration. Your number below will
              be saved.
            </p>
          )}

          {step === 'idle' && (
            <>
              <div className="cg-setting-row" style={{ marginTop: 0 }}>
                <label className="cg-field-label" htmlFor="wa-phone">
                  Your WhatsApp number
                </label>
                <input
                  id="wa-phone"
                  className="cg-input"
                  type="tel"
                  placeholder="+9779812345678"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && phone.trim().length >= 6) void sendCode();
                  }}
                />
              </div>
              {error && (
                <p style={{ color: 'var(--cg-error)', fontSize: '0.8125rem', margin: '4px 0 8px' }}>
                  {error}
                </p>
              )}
              <button
                type="button"
                className="cg-connect-btn"
                style={{ display: 'block' }}
                disabled={busy || phone.trim().length < 6}
                onClick={() => void sendCode()}
              >
                {busy ? 'Sending…' : 'Send code'}
              </button>
            </>
          )}

          {step === 'code' && (
            <>
              <p style={{ margin: '0 0 12px', fontSize: '0.875rem', color: 'var(--cg-on-surface-variant)', lineHeight: 1.5 }}>
                We sent a 6-digit code to <strong>+{connectedPhone}</strong> on WhatsApp.
                Enter it below.
              </p>
              <div className="cg-setting-row" style={{ marginTop: 0 }}>
                <label className="cg-field-label" htmlFor="wa-code">
                  Verification code
                </label>
                <input
                  id="wa-code"
                  className="cg-input"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="123456"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && code.length === 6) void verify();
                  }}
                />
              </div>
              {error && (
                <p style={{ color: 'var(--cg-error)', fontSize: '0.8125rem', margin: '4px 0 8px' }}>
                  {error}
                </p>
              )}
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 4 }}>
                <button
                  type="button"
                  className="cg-connect-btn"
                  disabled={busy || code.length !== 6}
                  onClick={() => void verify()}
                >
                  {busy ? 'Verifying…' : 'Verify'}
                </button>
                <button
                  type="button"
                  className="cg-connect-btn"
                  style={{ color: 'var(--cg-on-surface-variant)' }}
                  disabled={busy}
                  onClick={() => void sendCode()}
                >
                  Resend code
                </button>
                <button
                  type="button"
                  className="cg-connect-btn"
                  style={{ color: 'var(--cg-on-surface-variant)' }}
                  disabled={busy}
                  onClick={() => {
                    setStep('idle');
                    setCode('');
                    setError('');
                  }}
                >
                  Change number
                </button>
              </div>
            </>
          )}

          {step === 'connected' && (
            <>
              <p style={{ margin: '0 0 12px', fontSize: '0.875rem', color: 'var(--cg-on-surface-variant)', lineHeight: 1.5 }}>
                Storefront chats are forwarded to <strong>+{connectedPhone}</strong>.
                Reply to any message there and it appears in the widget.
              </p>
              <button
                type="button"
                className="cg-connect-btn"
                style={{ color: 'var(--cg-error)', display: 'block' }}
                disabled={busy}
                onClick={() => void disconnect()}
              >
                {busy ? 'Disconnecting…' : 'Disconnect'}
              </button>
            </>
          )}
        </div>
      )}
    </>
  );
}
